import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import { $accountPaymentListStore, $reportStore, $accountPaymentReportStore, $paymentStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useListState } from '@shared/hooks';
import { AccountUtils, cn, FormatUtils, LocalStorageUtils, PaletteUtils, PermissionUtils } from '@shared/utils';
import { AccountType, PaymentReportColumnsEnum, ReportExportFileType, type AccountResponse, type GeneratePaymentReportRequest } from '@shared/api';
import { ReportSecondaryTabs } from '../../models';
import { DepositAccountTable, DepositTransactionTable } from './';
import { ColumnsModal } from '../columnsModal';
import { ExportModal } from '../exportModal';
import { GeneratedFileModal } from '../generatedFileModal';
import { Button, Icon, SearchInput, Select, SelectDateRange, Spinner, Tabs } from '@shared/widgets';
import DownloadIcon from '@assets/icons/download.svg';
import PlusIcon from '@assets/icons/plus.svg';

const ColumnsEnum = {
  Status: 'Status',
  ReferenceNumber: 'ReferenceNumber',
  Date: 'Date',
  AccountNumber: 'AccountNumber',
  Direction: 'Direction',
  TypeOfTransfer: 'TypeOfTransfer',
  TransactionReference: 'TransactionReference',
  SenderIban: 'SenderIban',
  BeneficiaryIban: 'BeneficiaryIban',
  ExchangeRate: 'ExchangeRate',
  SenderAmount: 'SenderAmount',
  BeneficiaryAmount: 'BeneficiaryAmount',
  Fee: 'Fee',
  TotalAmount: 'TotalAmount',
} as const;
type ColumnsEnum = (typeof ColumnsEnum)[keyof typeof ColumnsEnum];

const COLUMNS = [
  ColumnsEnum.Status,
  ColumnsEnum.ReferenceNumber,
  ColumnsEnum.Date,
  ColumnsEnum.AccountNumber,
  ColumnsEnum.Direction,
  ColumnsEnum.TypeOfTransfer,
  ColumnsEnum.TransactionReference,
  ColumnsEnum.SenderIban,
  ColumnsEnum.BeneficiaryIban,
  ColumnsEnum.ExchangeRate,
  ColumnsEnum.SenderAmount,
  ColumnsEnum.BeneficiaryAmount,
  ColumnsEnum.Fee,
  ColumnsEnum.TotalAmount,
];

const mapColumnToEnum = (column: string): PaymentReportColumnsEnum | undefined => {
  const columns: { [key: string]: PaymentReportColumnsEnum } = {
    [ColumnsEnum.Status]: PaymentReportColumnsEnum.Status,
    [ColumnsEnum.ReferenceNumber]: PaymentReportColumnsEnum.ReferenceNumber,
    [ColumnsEnum.Date]: PaymentReportColumnsEnum.Date,
    [ColumnsEnum.AccountNumber]: PaymentReportColumnsEnum.AccountNumber,
    [ColumnsEnum.Direction]: PaymentReportColumnsEnum.Direction,
    [ColumnsEnum.TypeOfTransfer]: PaymentReportColumnsEnum.TypeOfTransfer,
    [ColumnsEnum.TransactionReference]: PaymentReportColumnsEnum.TransactionReference,
    [ColumnsEnum.SenderIban]: PaymentReportColumnsEnum.SenderIban,
    [ColumnsEnum.BeneficiaryIban]: PaymentReportColumnsEnum.BeneficiaryIban,
    [ColumnsEnum.ExchangeRate]: PaymentReportColumnsEnum.ExchangeRate,
    [ColumnsEnum.SenderAmount]: PaymentReportColumnsEnum.SenderAmount,
    [ColumnsEnum.BeneficiaryAmount]: PaymentReportColumnsEnum.BeneficiaryAmount,
    [ColumnsEnum.Fee]: PaymentReportColumnsEnum.Fee,
    [ColumnsEnum.TotalAmount]: PaymentReportColumnsEnum.TotalAmount,
  };

  return columns[column];
};

export const ReportDepositTab = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const accountsStore = useUnit($accountPaymentListStore.store);
  const paymentStore = useUnit($paymentStore.store);
  const accountReportStore = useUnit($accountPaymentReportStore.store);
  const reportStore = useUnit($reportStore.store);

  const [tab, setTab] = useState<ReportSecondaryTabs>();
  const [search, setSearch] = useState('');
  const [transactionColumns, setTransactionColumns] = useState<string[]>(LocalStorageUtils.getPaymentTransactionColumns() || COLUMNS);
  const [currency, setCurrency] = useState<string>();
  const [account, setAccount] = useState<AccountResponse | undefined>();
  const [dateRange, setDateRange] = useState<{ dateFrom?: Dayjs; dateTo?: Dayjs } | undefined>();
  const [showColumnsModal, setShowColumnsModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);

  const reportsPaymentsTransactionsState = useListState({ list: 'reportsPaymentsTransactions' });
  const reportsPaymentsAccountsState = useListState({ list: 'reportsPaymentsAccounts' });

  const { t } = useTranslation('reports');

  const accounts = useMemo(() => AccountUtils.getAccountsWithTransactionView(accountsStore.data), [accountsStore.data]);
  const isTransactionTab = tab === ReportSecondaryTabs.Transactions;

  useEffect(() => {
    if (PermissionUtils.viewReportsTransactions()) setTab(ReportSecondaryTabs.Transactions);
    else if (PermissionUtils.viewReportsAccountBalance()) setTab(ReportSecondaryTabs.AccountsBalance);

    $accountPaymentListStore.events.getAccounts({ AccountType: AccountType.Payment });

    return () => {
      $accountPaymentListStore.events.clear();
      $accountPaymentReportStore.events.clear();
      $paymentStore.events.clear();
    };
  }, []);

  const updateTransactions = () => {
    if (accounts.length === 0) return;
    $paymentStore.events.getPaymentsByAccounts({
      AccountIds: account ? [account.id] : accounts.map((account) => account.id),
      Currency: currency,
      DateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      DateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      SearchText: search,
      ReturnTotalCount: true,
      Count: reportsPaymentsTransactionsState.count,
      FromIndex: reportsPaymentsTransactionsState.fromIndex,
    });
  };
  useEffect(() => {
    if (tab !== ReportSecondaryTabs.Transactions || !PermissionUtils.viewReportsTransactions()) return;
    updateTransactions();
  }, [tab, currency, search, account, dateRange, reportsPaymentsTransactionsState, accounts]);

  const updateAccounts = () => {
    $accountPaymentReportStore.events.getPaymentAccountBalanceReport({
      Currency: currency,
      ReturnTotalCount: true,
      Count: reportsPaymentsAccountsState.count,
      FromIndex: reportsPaymentsAccountsState.fromIndex,
    });
  };
  useEffect(() => {
    if (tab !== ReportSecondaryTabs.AccountsBalance || !PermissionUtils.viewReportsAccountBalance()) return;
    updateAccounts();
  }, [tab, currency, reportsPaymentsAccountsState]);

  const exportHandler = async (item: ReportExportFileType) => {
    setShowExportModal(false);
    if (isTransactionTab) {
      const columnsVisible = transactionColumns.map(mapColumnToEnum).filter((col) => !!col);
      const request: GeneratePaymentReportRequest = {
        reportType: item,
        columnsVisible,
        accountIds: account ? [account.id] : accounts.map((account) => account.id),
        currency: currency,
        searchText: search,
        dateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        dateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      };
      const result = await $reportStore.events.generatePaymentReport(request);
      if (result.data) setShowGeneratedModal(true);
    } else {
      const result = await $reportStore.events.generatePaymentAccountBalanceReport({ ReportType: item, Currency: currency });
      if (result.data) setShowGeneratedModal(true);
    }
  };

  const changeTabHandler = (tab: ReportSecondaryTabs) => {
    setTab(tab);
    setCurrency(undefined);
  };

  const updateColumnsHandler = (activeColumns: string[]) => {
    setTransactionColumns(activeColumns);
    LocalStorageUtils.setPaymentTransactionColumns(activeColumns);
  };

  const tabOptions = useMemo(
    () =>
      [
        PermissionUtils.viewReportsTransactions() && { label: t('Transactions'), value: ReportSecondaryTabs.Transactions },
        PermissionUtils.viewReportsAccountBalance() && { label: t('Accounts Balance'), value: ReportSecondaryTabs.AccountsBalance },
      ].filter(Boolean) as { label: string; value: ReportSecondaryTabs }[],
    [t],
  );
  const accountOptions = useMemo(
    () => accounts.map((account) => ({ label: FormatUtils.formatAccountNumber(account.accountNumber), value: account })),
    [accounts],
  );
  return (
    <div className='flex h-full w-full flex-col'>
      <div className='my-4 flex items-center justify-between gap-2'>
        {isTransactionTab && <SearchInput withDebounce maxLength={128} value={search} onChange={setSearch} />}
        <div className='ml-auto flex gap-2'>
          {isTransactionTab && (
            <Button color='primary' onClick={() => setShowColumnsModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('buttons.addFilter')}
            </Button>
          )}
          <Button
            color='primary'
            disabled={isTransactionTab ? !paymentStore.data.data.length : !accountReportStore.data.data.length}
            onClick={() => setShowExportModal(true)}
          >
            <Icon icon={<DownloadIcon />} color='inherit' />
            {t('buttons.export')}
          </Button>
        </div>
      </div>
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && 'max-md:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName='h-full gap-0'
          labelClassName={cn('p-4', 'hover:bg-white dark:hover:bg-soft-black')}
          activeLabelClassName='bg-white dark:bg-soft-black'
          options={tabOptions}
          hideIndicator
          value={tab}
          onChange={changeTabHandler}
        />
        <div className={cn('flex gap-3 pb-6', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-2')}>
          <Select
            className='min-w-[160px]'
            isLoading={currenciesAvailableStore.loading}
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
          {isTransactionTab && (
            <>
              <Select
                className='min-w-[146px]'
                isLoading={accountsStore.loading}
                nullValue={{ isEnable: true, text: t('placeholders.allAccounts', { ns: 'common' }) }}
                items={accountOptions}
                value={account}
                onChange={setAccount}
              />
              <SelectDateRange value={dateRange} onChange={setDateRange} />
            </>
          )}
        </div>
      </div>
      <div className={cn('flex w-full', PaletteUtils.isVcfcPalette && 'flex-col rounded-b-xl bg-white p-6 dark:bg-soft-black')}>
        {PermissionUtils.viewReportsTransactions() && tab === ReportSecondaryTabs.Transactions && (
          <DepositTransactionTable
            isLoading={paymentStore.loading || accountsStore.loading}
            error={paymentStore.error}
            onRefresh={updateTransactions}
            columns={transactionColumns}
            items={paymentStore.data.data}
            totalItemCount={paymentStore.data.total}
            currentPage={reportsPaymentsTransactionsState.page}
            countItemPage={reportsPaymentsTransactionsState.count}
            onChangePage={reportsPaymentsTransactionsState.onChangePage}
            onChangeCountItemPage={reportsPaymentsTransactionsState.onChangeCount}
          />
        )}
        {PermissionUtils.viewReportsAccountBalance() && tab === ReportSecondaryTabs.AccountsBalance && (
          <DepositAccountTable
            isLoading={accountReportStore.loading}
            error={accountReportStore.error}
            onRefresh={updateAccounts}
            accounts={accountReportStore.data.data}
            totalItemCount={accountReportStore.data.total}
            currentPage={reportsPaymentsAccountsState.page}
            countItemPage={reportsPaymentsAccountsState.count}
            onChangePage={reportsPaymentsAccountsState.onChangePage}
            onChangeCountItemPage={reportsPaymentsAccountsState.onChangeCount}
          />
        )}
      </div>
      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} onExport={exportHandler} />
      {reportStore.loading && <Spinner position />}
      <ColumnsModal
        show={showColumnsModal}
        onClose={() => setShowColumnsModal(false)}
        columns={COLUMNS}
        activeColumns={transactionColumns}
        onSave={updateColumnsHandler}
      />
      <GeneratedFileModal open={showGeneratedModal} onClose={() => setShowGeneratedModal(false)} />
    </div>
  );
};
